import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Reveal, ChapterTag } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_ENDPOINT = (typeof window !== "undefined" && window.__CONTACT_ENDPOINT__) || `${API}/contact`;

const fieldClass =
  "bg-white/5 border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-brand focus-visible:border-brand rounded-md h-12";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(CONTACT_ENDPOINT, form);
      toast.success(c.toastOk);
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error(c.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <ChapterTag index="04" label={c.tag} />
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-white mt-6" data-testid="contact-heading">
            {c.heading}
          </h2>
          <p className="mt-8 text-neutral-400 leading-relaxed max-w-md">
            {c.body}
          </p>
          <div className="mt-10 space-y-4 font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            <p data-testid="contact-response-time">{c.responseLabel}: <span className="text-brand-light">{c.responseValue}</span></p>
            <p data-testid="contact-availability">{c.supportLabel}: <span className="text-brand-light">{c.supportValue}</span></p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="border border-white/10 rounded-xl bg-[#0A0A0F] p-8 lg:p-10 space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{c.labels.name}</Label>
                <Input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={c.placeholders.name} className={fieldClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{c.labels.email}</Label>
                <Input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={c.placeholders.email} className={fieldClass} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="contact-company" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{c.labels.company}</Label>
                <Input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder={c.placeholders.company} className={fieldClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-service" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{c.labels.service}</Label>
                <select
                  id="contact-service"
                  data-testid="contact-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className="w-full h-12 rounded-md bg-white/5 border border-white/15 text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="" className="bg-[#0A0A0F]">{c.placeholders.service}</option>
                  {c.services.map((s) => (
                    <option key={s} value={s} className="bg-[#0A0A0F]">{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">{c.labels.message}</Label>
              <Textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={c.placeholders.message} className="bg-white/5 border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-brand focus-visible:border-brand rounded-md" />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light disabled:opacity-60 text-white font-mono text-xs uppercase tracking-[0.25em] px-7 py-4 rounded-full transition-[background-color,transform,box-shadow] duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(224,18,127,0.45)]"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
              {sending ? c.sending : c.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
